import { Broker, NestedAutocompleteAddress, NestedUser } from '@habity/types';
import { useCallback, useEffect, useRef } from 'react';
import Cal, { getCalApi } from '@calcom/embed-react';
import { isDev } from '~/lib/utils';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { isSharkTankBroker } from '~/lib/sell/sharkTankBroker';

// Re-export Cal component for use in modals
export { Cal };

// Cal.com instance URL
export const CAL_ORIGIN = 'https://meet.erikolsson.se';
export const CAL_EMBED_URL = 'https://meet.erikolsson.se/embed/embed.js';

export interface CalAddress {
  street?: string;
  city?: string;
  zip?: string;
  full?: string;
  apartmentNumber?: string;
  category?: string;
  ownership?: string;
  livingSpace?: number | null;
  rooms?: number | null;
}

export interface CalBookingOptions {
  user?: NestedUser | null;
  address?: CalAddress | null;
  notes?: string;
  customData?: Record<string, string>;
  eventType?: string; // Optional event type to append to cal link (e.g., 'vardering')
}

/**
 * Check if a broker has Cal.com calendar enabled.
 *
 * Conditions:
 * - Must have a selected broker
 * - Broker must have completed Cal.com onboarding
 * - Broker must have a valid Cal.com username
 *
 * @param selectedBroker - The selected broker object
 * @returns true if the broker has Cal.com calendar enabled
 */
export function hasBrokerCalendar(
  selectedBroker?: Broker | Partial<Broker> | null
): boolean {
  if (!selectedBroker) {
    return false;
  }

  // Fallback shark tank brokers don't have real calendars in production
  // Allow in dev for testing
  if (
    isSharkTankBroker(selectedBroker) &&
    process.env.NODE_ENV !== 'development'
  ) {
    return false;
  }

  if (!selectedBroker.cal_onboarding_completed) {
    return false;
  }

  if (!selectedBroker.cal_username) {
    return false;
  }

  return true;
}

/**
 * @deprecated Use hasBrokerCalendar instead - intent check is no longer needed
 */
export function shouldLoadCalCalendar(
  _intent: string | null | undefined,
  selectedBroker?: Broker | Partial<Broker> | null
): boolean {
  return hasBrokerCalendar(selectedBroker);
}

/**
 * Normalize and validate phone number for Cal.com
 * Cal.com expects E.164 format (e.g., +46701234567)
 * Returns null if phone is invalid
 */
function formatPhoneForCal(phone: string | null | undefined): string | null {
  if (!phone) return null;

  // Clean the phone number
  let cleaned = phone.replace(/[\s\-()]/g, '');

  // Normalize Swedish phone numbers (add +46 if starts with 0)
  if (/^0[1-9]/.test(cleaned)) {
    cleaned = '+46' + cleaned.substring(1);
  }

  // Parse and validate
  const parsed = parsePhoneNumber(cleaned, { regionCode: 'SE' });

  // Only return if valid, in E.164 format
  if (parsed.valid) {
    return parsed.number?.e164 || null;
  }

  return null;
}

/**
 * Build Cal.com configuration for prefilling user data
 */
export function buildCalConfig(
  options?: CalBookingOptions,
  hideEventTypeDetails = true
): Record<string, any> {
  const config: Record<string, any> = {
    layout: 'month_view',
    theme: 'light',
    hideEventTypeDetails,
  };

  if (options?.user) {
    const { user } = options;
    if (user.first_name) {
      config.name = `${user.first_name} ${user.last_name || ''}`.trim();
    }
    if (user.email) config.email = user.email;
    // Cal.com uses 'attendeePhoneNumber' for the phone booking question field
    // Only include if phone is valid and properly formatted
    const formattedPhone = formatPhoneForCal(user.phone);
    if (formattedPhone) config.attendeePhoneNumber = formattedPhone;
  }

  if (options?.address) {
    const { address } = options;
    // Build address string for location and address field
    let addressString = '';
    if (address.full) {
      addressString = address.full;
    } else if (address.street) {
      const parts = [
        address.street,
        [address.zip, address.city].filter(Boolean).join(' '),
      ].filter(Boolean);
      addressString = parts.join(', ');
    }

    if (addressString) {
      // Always prefill the custom address booking field (present on all event types, hidden by default).
      // The booking form dynamically shows it when the location is not attendeeInPerson.
      config.address = addressString;

      // For in-person events: also prefill the attendeeInPerson location field.
      // Cal.com parses radioInput values from JSON strings in query params.
      const isInPerson =
        options?.eventType === 'valuation' || options?.eventType === 'meeting';
      if (isInPerson) {
        config.location = JSON.stringify({
          value: 'attendeeInPerson',
          optionValue: addressString,
        });
      }
    }

    // Prefill apartment number booking question field
    // Always set to empty string if not provided to prevent Cal.com from showing "undefined"
    config.apartmentNumber = address.apartmentNumber || '';

    // Prefill category as a hidden booking question field
    if (address.category) {
      config.category = address.category;
    }

    // Prefill ownership as a hidden booking question field
    if (address.ownership) {
      config.ownership = address.ownership;
    }

    // Prefill living space as a hidden booking question field
    if (address.livingSpace) {
      config.livingSpace = String(address.livingSpace);
    }

    // Prefill rooms as a hidden booking question field
    if (address.rooms) {
      config.rooms = String(address.rooms);
    }
  }

  // Prefill notes with property data
  if (options?.notes) {
    config.notes = options.notes;
  }

  // Add custom data
  if (options?.customData) {
    Object.assign(config, options.customData);
  }

  return config;
}

/**
 * Build a notes string from property/address data for Cal.com bookings.
 * Includes all available property details like category, rooms, living space, etc.
 */
export function buildPropertyNotes(
  address: NestedAutocompleteAddress | null | undefined,
  intent?: string
): string {
  if (!address) return '';

  const lines: string[] = [];

  if (intent) {
    const intentLabels: Record<string, string> = {
      valuation: 'Värdering',
      meeting: 'Möte',
      estimate: 'Prisindikation',
      claim: 'Testa marknaden',
    };
    lines.push(`Ärende: ${intentLabels[intent] || intent}`);
  }

  if (address.street) lines.push(`Adress: ${address.street}`);
  if (address.apartment_number) lines.push(`Lgh: ${address.apartment_number}`);
  if (address.zip || address.city) {
    lines.push(`Ort: ${[address.zip, address.city].filter(Boolean).join(' ')}`);
  }
  if (address.category) {
    const categoryLabels: Record<string, string> = {
      apartment: 'Lägenhet',
      house: 'Villa',
      townhouse: 'Radhus',
      vacation: 'Fritidshus',
      plot: 'Tomt',
      farm: 'Gård',
      other: 'Övrigt',
    };
    lines.push(`Typ: ${categoryLabels[address.category] || address.category}`);
  }
  if (address.rooms) lines.push(`Rum: ${address.rooms}`);
  if (address.living_space) lines.push(`Boarea: ${address.living_space} kvm`);
  if (address.build_year) lines.push(`Byggår: ${address.build_year}`);
  if (address.ownership) {
    const ownershipLabels: Record<string, string> = {
      Owner: 'Äganderätt',
      TenantOwnership: 'Bostadsrätt',
      Tenancy: 'Hyresrätt',
      SiteLeasehold: 'Tomträtt',
      Unknown: 'Okänd',
    };
    lines.push(
      `Upplåtelseform: ${
        ownershipLabels[address.ownership] || address.ownership
      }`
    );
  }

  return lines.join('\n');
}

/**
 * Build Cal.com link (username + optional event type)
 */
export function buildCalLink(calUsername: string, eventType?: string): string {
  return eventType ? `${calUsername}/${eventType}` : calUsername;
}

/**
 * Initialize Cal.com globally (call once on app load)
 */
export async function initCalGlobally(
  hideEventTypeDetails = true
): Promise<void> {
  try {
    const cal = await getCalApi({ embedJsUrl: CAL_EMBED_URL });
    cal('ui', {
      hideEventTypeDetails,
      layout: 'month_view',
    });

    if (isDev) {
      console.log('[Cal.com] Initialized globally');
    }
  } catch (error) {
    console.error('[Cal.com] Failed to initialize globally:', error);
    throw error;
  }
}

/**
 * Build data-cal attributes for a button/element
 * Use this to enable click-to-book functionality on any element
 * Note: Don't use namespace - it requires separate initialization
 */
export function buildCalDataAttributes(
  calUsername: string,
  options?: CalBookingOptions
): Record<string, string> {
  const calLink = buildCalLink(calUsername, options?.eventType);
  const config = buildCalConfig(options);

  const attributes: Record<string, string> = {
    'data-cal-link': calLink,
    'data-cal-origin': CAL_ORIGIN,
  };

  // Add config as JSON string
  if (Object.keys(config).length > 0) {
    attributes['data-cal-config'] = JSON.stringify(config);
  }

  return attributes;
}

interface UseCalOptions {
  hideEventTypeDetails?: boolean;
  onBookingSuccess?: (data: any) => void;
}

/**
 * Hook to integrate Cal.com calendars for brokers
 * Provides utility functions for imperative API usage
 */
export const useCal = (
  hideEventTypeDetailsOrOptions?: boolean | UseCalOptions
) => {
  // Support both old boolean signature and new options object
  const options: UseCalOptions =
    typeof hideEventTypeDetailsOrOptions === 'boolean'
      ? { hideEventTypeDetails: hideEventTypeDetailsOrOptions }
      : hideEventTypeDetailsOrOptions || {};

  const hideEventTypeDetails = options.hideEventTypeDetails ?? true;
  const onBookingSuccess = options.onBookingSuccess;

  // Store the callback in a ref to avoid re-registering listeners
  const onBookingSuccessRef = useRef(onBookingSuccess);
  useEffect(() => {
    onBookingSuccessRef.current = onBookingSuccess;
  }, [onBookingSuccess]);

  // Set up event listener for booking success
  // Track if we have a callback to avoid unnecessary listener registration
  const hasCallback = !!onBookingSuccess;

  useEffect(() => {
    // Only set up listener if callback is provided
    if (!hasCallback) return;

    const handleBookingSuccess = (e: MessageEvent) => {
      // Cal.com sends events via postMessage
      if (e.origin !== CAL_ORIGIN) return;

      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;

        // Check if this is a booking success event
        if (
          data?.type === 'bookingSuccessful' ||
          data?.event === 'bookingSuccessful'
        ) {
          if (isDev) {
            console.log('[Cal.com] Booking successful:', data);
          }
          onBookingSuccessRef.current?.(data);
        }
      } catch (error) {
        // Ignore parse errors from other postMessage sources
        if (isDev) {
          console.debug('[Cal.com] Ignored non-JSON message:', e.data);
        }
      }
    };

    window.addEventListener('message', handleBookingSuccess);

    return () => {
      window.removeEventListener('message', handleBookingSuccess);
    };
  }, [hasCallback]); // Re-register if callback presence changes

  /**
   * Open Cal.com booking in a modal/popup using imperative API
   * (Alternative to using CalButton with data-cal attributes)
   */
  const openModal = useCallback(
    async (calUsername: string, options?: CalBookingOptions) => {
      const calLink = buildCalLink(calUsername, options?.eventType);
      const config = buildCalConfig(options, hideEventTypeDetails);

      try {
        const cal = await getCalApi({
          namespace: options?.eventType,
          embedJsUrl: CAL_EMBED_URL,
        });

        cal('ui', {
          hideEventTypeDetails,
          layout: 'month_view',
        });

        // Open modal
        cal('modal', {
          calLink,
          config,
        });

        if (isDev) {
          console.log('[Cal.com] Modal opened:', {
            calUsername,
            eventType: options?.eventType,
          });
        }
      } catch (error) {
        console.error('[Cal.com] Failed to open modal:', error);
        throw error;
      }
    },
    [hideEventTypeDetails]
  );

  return {
    openModal,
  };
};
